function dirOf(side) {
    return side === "long" ? 1 : -1;
}
export class PositionManager {
    hedgingMode;
    multiplier;
    costBasisMethod;
    positions = new Map();
    /**
     * S5: sidecar FIFO lots keyed by position id (không đụng TradingPosition
     * type → storage tương thích; vị thế mở trước khi bật fifo giữ average).
     */
    lots = new Map();
    seq = 0;
    lotSeq = 0;
    constructor(options = {}) {
        this.hedgingMode = options.hedgingMode ?? false;
        this.multiplier = options.contractMultiplier ?? 1;
        this.costBasisMethod = options.costBasisMethod ?? "average";
    }
    /** S2/S5: đổi method — chỉ áp dụng vị thế MỞ MỚI (prospective, §10 Q2). */
    setCostBasisMethod(method) {
        this.costBasisMethod = method;
    }
    clear() {
        this.positions.clear();
        this.lots.clear();
        this.seq = 0;
        this.lotSeq = 0;
    }
    /**
     * S5: khớp đóng `quantity` vào các lô cũ nhất, trả realized + cập nhật
     * entryPrice = bình quân gia quyền các lô còn lại (giữ updateMarkPrice
     * và mọi công thức hiển thị nguyên vẹn ở cả 2 mode).
     */
    matchCloseLots(position, quantity, exitPrice) {
        const positionLots = this.lots.get(position.id);
        if (!positionLots || positionLots.length === 0) {
            return (exitPrice - position.entryPrice) * dirOf(position.side) * quantity * this.multiplier;
        }
        let remaining = quantity;
        let realized = 0;
        while (remaining > 0 && positionLots.length > 0) {
            const lot = positionLots[0];
            const take = Math.min(remaining, lot.quantity);
            realized += (exitPrice - lot.price) * dirOf(position.side) * take * this.multiplier;
            lot.quantity -= take;
            remaining -= take;
            if (lot.quantity <= 0)
                positionLots.shift();
        }
        const leftQty = positionLots.reduce((sum, lot) => sum + lot.quantity, 0);
        if (leftQty > 0) {
            position.entryPrice =
                positionLots.reduce((sum, lot) => sum + lot.quantity * lot.price, 0) / leftQty;
        }
        return realized;
    }
    trackLot(position, quantity, price, timestamp) {
        const positionLots = this.lots.get(position.id) ?? [];
        positionLots.push({ lotId: `lot_${++this.lotSeq}`, quantity, price, timestamp });
        this.lots.set(position.id, positionLots);
    }
    /** Hydrate từ storage (Task 06). */
    restoreState(positions) {
        this.clear();
        for (const position of positions) {
            this.positions.set(position.id, { ...position });
            const n = Number(/^pos_(\d+)$/.exec(position.id)?.[1]);
            if (Number.isInteger(n) && n >= this.seq)
                this.seq = n + 1;
        }
    }
    listPositions() {
        return [...this.positions.values()];
    }
    getPositionById(positionId) {
        return this.positions.get(positionId);
    }
    getPosition(symbol) {
        return this.listPositions().find((p) => p.symbol === symbol);
    }
    openPosition(request) {
        if (!Number.isFinite(request.quantity) || request.quantity <= 0) {
            throw new Error("Position quantity must be positive");
        }
        if (!Number.isFinite(request.price) || request.price <= 0) {
            throw new Error("Position price must be positive");
        }
        const sameSide = this.listPositions().find((p) => p.symbol === request.symbol && p.side === request.side);
        if (sameSide) {
            sameSide.entryPrice =
                (sameSide.quantity * sameSide.entryPrice + request.quantity * request.price) /
                    (sameSide.quantity + request.quantity);
            sameSide.quantity += request.quantity;
            sameSide.availableQuantity += request.settlementDate == null ? request.quantity : 0;
            sameSide.updatedAt = request.timestamp;
            if (this.costBasisMethod === "fifo" && this.lots.has(sameSide.id)) {
                this.trackLot(sameSide, request.quantity, request.price, request.timestamp);
            }
            return sameSide;
        }
        if (!this.hedgingMode) {
            const opposite = this.listPositions().find((p) => p.symbol === request.symbol);
            if (opposite)
                return this.offsetOpposite(opposite, request);
        }
        return this.createPosition(request);
    }
    offsetOpposite(existing, request) {
        const dir = dirOf(existing.side);
        const fifo = this.costBasisMethod === "fifo" && this.lots.has(existing.id);
        if (request.quantity < existing.quantity) {
            const realized = fifo
                ? this.matchCloseLots(existing, request.quantity, request.price)
                : (request.price - existing.entryPrice) * dir * request.quantity * this.multiplier;
            existing.quantity -= request.quantity;
            existing.availableQuantity = Math.max(0, existing.availableQuantity - request.quantity);
            existing.realizedPnl += realized;
            existing.updatedAt = request.timestamp;
            return existing;
        }
        const realized = fifo
            ? this.matchCloseLots(existing, existing.quantity, request.price)
            : (request.price - existing.entryPrice) * dir * existing.quantity * this.multiplier;
        this.positions.delete(existing.id);
        this.lots.delete(existing.id);
        const remainder = request.quantity - existing.quantity;
        const realizedTotal = existing.realizedPnl + realized;
        if (remainder === 0) {
            return { ...existing, quantity: 0, availableQuantity: 0, realizedPnl: realizedTotal };
        }
        const reversed = this.createPosition({
            symbol: request.symbol,
            side: request.side,
            quantity: remainder,
            price: request.price,
            timestamp: request.timestamp,
        });
        reversed.realizedPnl = realizedTotal;
        return reversed;
    }
    createPosition(request) {
        const position = {
            id: `pos_${++this.seq}`,
            symbol: request.symbol,
            side: request.side,
            quantity: request.quantity,
            availableQuantity: request.settlementDate == null ? request.quantity : 0,
            entryPrice: request.price,
            currentPrice: request.price,
            unrealizedPnl: 0,
            realizedPnl: 0,
            entryFees: request.entryFees ?? 0,
            settlementDate: request.settlementDate,
            createdAt: request.timestamp,
            updatedAt: request.timestamp,
        };
        this.positions.set(position.id, position);
        if (this.costBasisMethod === "fifo") {
            this.trackLot(position, request.quantity, request.price, request.timestamp);
        }
        return position;
    }
    closePosition(positionId, quantity, exitPrice, timestamp, _reason, options) {
        const position = this.positions.get(positionId);
        if (!position)
            throw new Error(`Position not found: ${positionId}`);
        const enforceAvailable = options?.enforceAvailable ?? true;
        const limit = enforceAvailable ? position.availableQuantity : position.quantity;
        if (!Number.isFinite(quantity) || quantity <= 0 || quantity > limit) {
            throw new Error("Close quantity exceeds available quantity");
        }
        const fifo = this.costBasisMethod === "fifo" && this.lots.has(position.id);
        const realized = fifo
            ? this.matchCloseLots(position, quantity, exitPrice)
            : (exitPrice - position.entryPrice) * dirOf(position.side) * quantity * this.multiplier;
        const quantityBefore = position.quantity;
        position.quantity -= quantity;
        position.availableQuantity = Math.max(0, position.availableQuantity - quantity);
        position.realizedPnl += realized;
        position.updatedAt = timestamp;
        // S5: the entry fee is pro-rated onto the quantity being closed, so a partial exit
        // carries its share and the rest stays on the position for the next close.
        const heldEntryFees = position.entryFees ?? 0;
        const entryFees = quantityBefore > 0 ? (heldEntryFees * quantity) / quantityBefore : 0;
        const remainingEntryFees = heldEntryFees - entryFees;
        if (position.quantity === 0) {
            this.positions.delete(positionId);
            this.lots.delete(positionId);
            return { closedQuantity: quantity, realizedPnl: realized, entryFees, remaining: null };
        }
        position.entryFees = remainingEntryFees;
        return {
            closedQuantity: quantity,
            realizedPnl: realized,
            entryFees,
            remaining: position,
        };
    }
    updateMarkPrice(symbol, price, timestamp) {
        for (const position of this.positions.values()) {
            if (position.symbol !== symbol)
                continue;
            position.currentPrice = price;
            position.unrealizedPnl = (price - position.entryPrice) * dirOf(position.side) * position.quantity * this.multiplier;
            position.updatedAt = timestamp;
        }
    }
    applySettlement(now) {
        for (const position of this.positions.values()) {
            if (position.settlementDate != null && now >= position.settlementDate) {
                position.availableQuantity = position.quantity;
                position.updatedAt = now;
            }
        }
    }
}
